import { useTranslation } from "@refinedev/core";

import { Avatar, Box, Button, Chip, Stack, Typography, styled } from "@mui/material";

import type { MfaMethodType } from "@/hooks/useMfaResource";

import { MfaSecurityChip } from "./MfaSecurityChip";

export interface ProfileMfaMethodCardProps {
  method: MfaMethodType;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  statusBadge: "configured" | "not-configured";
  /** Optional security level tag (e.g. "Most Secure", "Less Secure") */
  securityTag?: {
    label: string;
    variant: "success" | "error";
  };
  /** Optional content rendered below the clickable area, inside the card border */
  extraContent?: React.ReactNode;
  onClick: (method: MfaMethodType) => void;
  isExpanded: boolean;
}

// Styled card wrapper (border on outer container for extraContent support)
const CardWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  border: isExpanded ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

const CardButtonStyled = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.25),
  border: "none",
  borderRadius: 0,
  backgroundColor: "transparent",
  textTransform: "none",
  textAlign: "left",
  justifyContent: "flex-start",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const IconAvatar = styled(Avatar, {
  shouldForwardProp: prop => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  width: theme.spacing(5.25), // 42px
  height: theme.spacing(5.25), // 42px
  backgroundColor: "transparent",
  border: `1px solid ${isExpanded ? theme.palette.primary.main : theme.palette.grey[500]}`,
  color: isExpanded ? theme.palette.primary.main : theme.palette.grey[500],
  "& svg": {
    color: isExpanded ? theme.palette.primary.main : theme.palette.grey[500],
    fill: isExpanded ? theme.palette.primary.main : theme.palette.grey[500],
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== "isConfigured",
})<{ isConfigured: boolean }>(({ theme, isConfigured }) => ({
  height: "auto",
  borderRadius: theme.spacing(2), // 16px
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.grey[900],
  "& .MuiChip-label": {
    padding: theme.spacing(0.5, 1.25),
    ...theme.typography.caption,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.625), // 5px
  },
  "& .MuiChip-icon": {
    marginLeft: theme.spacing(1.25),
    marginRight: 0,
    width: theme.spacing(1), // 8px
    height: theme.spacing(1), // 8px
    borderRadius: "50%",
    backgroundColor: isConfigured ? theme.palette.success.main : theme.palette.grey[500],
  },
}));

// Dot indicator for badge
const StatusDot = styled(Box, {
  shouldForwardProp: prop => prop !== "isConfigured",
})<{ isConfigured: boolean }>(({ theme, isConfigured }) => ({
  width: theme.spacing(1), // 8px
  height: theme.spacing(1), // 8px
  borderRadius: "50%",
  backgroundColor: isConfigured ? theme.palette.success.main : theme.palette.grey[500],
}));

/**
 * MFA Method Card for Profile Page
 * - Shows method icon, title, description, and configuration badge with status dot
 * - Clickable card for expanding/collapsing details
 * - Blue border when expanded
 */
export const ProfileMfaMethodCard: React.FC<ProfileMfaMethodCardProps> = ({
  method,
  icon,
  title,
  description,
  statusBadge,
  securityTag,
  extraContent,
  onClick,
  isExpanded,
}) => {
  const { translate } = useTranslation();

  const handleClick = () => {
    onClick(method);
  };

  return (
    <CardWrapper isExpanded={isExpanded}>
      <CardButtonStyled onClick={handleClick}>
        <Stack direction="row" spacing={1.25} alignItems="center" width="100%">
          {/* Icon */}
          <IconAvatar isExpanded={isExpanded}>{icon}</IconAvatar>

          {/* Content (Title + Badge + Description) */}
          <Box flex={1}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
              <StatusChip
                label={translate(statusBadge === "configured" ? "common.configured" : "common.notConfigured")}
                icon={<StatusDot isConfigured={statusBadge === "configured"} />}
                size="small"
                isConfigured={statusBadge === "configured"}
              />
              {securityTag && (
                <MfaSecurityChip label={securityTag.label} size="small" tagVariant={securityTag.variant} />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" component="div">
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardButtonStyled>
      {extraContent}
    </CardWrapper>
  );
};
